import { type RootState } from '@/redux/store';
import { type AbtestLimitErrors, type FinanceActions } from '@marpla/core/enum';
import { checkIsMarplaUrl } from '@marpla/core/function/check-is-marpla-url';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

interface FinanceRedirectState {
  paymentAction: FinanceActions | null;
	settings: {
		mode: AbtestLimitErrors | null;
		failReturnUrl: string | null;
		returnUrl: string | null;
	};
  initialized: boolean;
  navigatePath: string | null;
}

const initialState: FinanceRedirectState = {
  paymentAction: null,
	settings: {
		mode: null,
		failReturnUrl: null,
		returnUrl: null,
	},
  initialized: false,
  navigatePath: null,
};

const normalizeUrl = (url: string | null, isExternal: boolean): string | null => {
	if (!url) {
		return null;
	}

	const _url = url.trim();
	const isInternalPath = !!_url && _url.startsWith('/');

	return _url && (isExternal || isInternalPath || checkIsMarplaUrl(_url)) ? url : null;
}

export interface SetRedirectSettingsPayload {
	returnUrl: FinanceRedirectState['settings']['returnUrl'];
	mode?: FinanceRedirectState['settings']['mode'];
	failReturnUrl?: FinanceRedirectState['settings']['failReturnUrl'];
	isExternal: boolean;
}

const financeRedirectSlice = createSlice({
  name: 'financeRedirect',
  initialState,
  reducers: {
    setPaymentAction(state: FinanceRedirectState, action: PayloadAction<FinanceActions | null>) {
      state.paymentAction = action.payload ?? null;
    },
    setRedirectSettings(
      state: FinanceRedirectState,
      action: PayloadAction<SetRedirectSettingsPayload>,
    ) {
      const { returnUrl, isExternal, mode, failReturnUrl } = action.payload;
      const _returnUrl = normalizeUrl(returnUrl, isExternal);
			const _failReturnUrl = failReturnUrl ? normalizeUrl(failReturnUrl, isExternal) : _returnUrl;

      state.settings.returnUrl = _returnUrl;
			state.settings.failReturnUrl = _failReturnUrl;

			if (mode) {
				state.settings.mode = mode;
			}
    },
    markInitialized(state: FinanceRedirectState) {
      state.initialized = true;
    },
    setNavigatePath(state: FinanceRedirectState, action: PayloadAction<string | null>) {
      state.navigatePath = action.payload ?? null;
    },
    resetFinanceRedirect() {
      return initialState;
    },
  },
});

export const { setPaymentAction, setRedirectSettings, markInitialized, setNavigatePath, resetFinanceRedirect } = financeRedirectSlice.actions;

export const selectFinanceRedirectPaymentAction = (s: RootState): FinanceRedirectState['paymentAction'] => s.financeRedirect.paymentAction;
export const selectFinanceRedirectReturnUrl = (s: RootState): FinanceRedirectState['settings']['returnUrl'] => s.financeRedirect.settings.returnUrl;
export const selectFinanceRedirectMode = (s: RootState): FinanceRedirectState['settings']['mode'] => s.financeRedirect.settings.mode;
export const selectFinanceRedirectSettings = (s: RootState): FinanceRedirectState['settings'] => s.financeRedirect.settings;
export const selectFinanceRedirectInitialized = (s: RootState): FinanceRedirectState['initialized'] => s.financeRedirect.initialized;
export const selectFinanceRedirectNavigatePath = (s: RootState): FinanceRedirectState['navigatePath'] => s.financeRedirect.navigatePath;

export type RedirectToReturnUrlInput = 'success' | 'failed';
export const navigateOutsideFinance = (type: RedirectToReturnUrlInput = 'success') => (dispatch: any, getState: () => RootState) => {
  const { returnUrl, failReturnUrl } = getState().financeRedirect.settings;
	const urlMap = {
		success: returnUrl,
		failed: failReturnUrl,
	}
	const url = urlMap[type];

  if (!url) return;

  if (url.startsWith('/')) {
    dispatch(setNavigatePath(url));
  } else {
		window.location.href = url;
  }
};

export default financeRedirectSlice.reducer;
